import { makeObservable, observable, observe, runInAction } from 'mobx';
import { captureException } from '@sentry/react';
import {
  getBookkeepingAccounts,
  getBookkeepingIntegration,
  getVouchers,
  retryVoucher,
} from '../../api/admin';
import { BookkeeperTypes } from '../../constants';
import { VoucherHistory } from '../../model/BookkeepingVoucher';
import CompanyStore from '../CompanyStore';
import FortnoxBookkeepingStore from './fortnoxBookkeepingStore';
import VismaBookkeepingStore from './vismaBookkeepingStore';

class BookkeepingStore {
  companyStore: CompanyStore;

  loading = false;

  bookkeeper: string | undefined;

  integrationStore: FortnoxBookkeepingStore | VismaBookkeepingStore | null = null;

  vouchers: VoucherHistory[] = [];

  isVouchersLoading = false;

  voucherIndex = 0;

  isRetryingVoucher = false;

  accountList = [];

  isAccountsLoading = false;

  constructor(companyStore: CompanyStore) {
    this.companyStore = companyStore;
    makeObservable(this, {
      loading: observable,
      bookkeeper: observable,
      integrationStore: observable,
      vouchers: observable,
      voucherIndex: observable,
      isVouchersLoading: observable,
      isRetryingVoucher: observable,
      accountList: observable,
      isAccountsLoading: observable,
    });
    observe(this.companyStore, 'companyKey', async ({ newValue }) => {
      if (newValue) {
        this.initialize();
      } else {
        this.clear();
      }
    });
  }

  async initialize() {
    if (!this.companyStore.companyKey) {
      return;
    }

    const integration = await getBookkeepingIntegration(this.companyStore.companyKey).catch(
      (err) => {
        captureException(err);
      },
    );
    if (integration) {
      runInAction(() => {
        this.integrationStore = null;
        if (integration.bookkeeper === BookkeeperTypes.fortnox) {
          this.integrationStore = new FortnoxBookkeepingStore(this.companyStore);
          this.getHistory();
        } else if (integration.bookkeeper === BookkeeperTypes.visma) {
          this.integrationStore = new VismaBookkeepingStore(this.companyStore);
        }
      });
    }
    await this.loadAccounts();
  }

  clear() {
    this.integrationStore = null;
  }

  async getHistory() {
    if (!this.companyStore.companyKey) {
      return;
    }
    if (this.integrationStore) {
      this.isVouchersLoading = true;
      const result = await getVouchers(this.companyStore.companyKey, this.voucherIndex);
      this.vouchers = result.vouchers;
      this.voucherIndex = result.index;
      this.isVouchersLoading = false;
    }
  }

  async retryVoucher(voucherKey: string) {
    this.isRetryingVoucher = true;
    if (!this.companyStore.companyKey) {
      return;
    }
    const result = await retryVoucher(this.companyStore.companyKey, voucherKey);
    if (result) {
      const index = this.vouchers.findIndex((voucher) => voucher.id === result.id);
      this.vouchers[index] = result;
    }
    this.isRetryingVoucher = false;
  }

  async loadAccounts() {
    if (!this.companyStore.companyKey) {
      return;
    }

    this.isAccountsLoading = true;

    this.accountList = await getBookkeepingAccounts(this.companyStore.companyKey)
      .catch((error) => {
        captureException(error);
        return [];
      })
      .finally(() => {
        runInAction(() => {
          this.isAccountsLoading = false;
        });
      });
  }
}

export default BookkeepingStore;
